import { reactive, toJS } from '@rehaje/reactive'
import {
  getIn,
  getProxyRaw,
  isPrivateKey,
  isProxyRawKey,
  toJSON,
  uid
} from '@rehaje/shared'

import { LifeCycle } from './LifeCycle'

import type { Store } from '../stores/Store'
import type { InternalNode, ScopeTypes } from '../types'
import type { TypedNode } from './TypedNode'
import type { ViewElement } from './ViewElement'
import type { ViewModel } from './ViewModel'
import type { Filter, PathLikes, TRecord } from '@rehaje/shared'

/**
 * 视图作用域模型，主要面向 UI 型 DSL 的视图作用域数据管理
 */
export class ViewScope extends LifeCycle implements InternalNode<ViewScope> {
  override __r_debug_id = `viewScope:${uid()}`

  /**
   * 响应式临时变量
   */
  __r_local_variables?: TRecord

  /**
   * 元素节点
   */
  element: ViewElement

  /**
   * 路径地址
   */
  get address() {
    return this.element.address
  }

  get block(): ViewScope {
    if (this.type === 'Block') return this
    return this.parent?.block ?? /* v8 ignore next */ this
  }

  /**
   * 子作用域集合
   */
  @reactive('computed')
  get children(): ViewScope[] {
    return this.element.children.map((node) => node.scope)
  }

  @reactive('computed')
  get dependencies() {
    const accessors = this.typings.__r_dependency_accessors
    return new Proxy(accessors, {
      get: (target, key) => {
        if (isProxyRawKey(key)) return getProxyRaw(target)
        const value = target[key]
        if (typeof value === 'function') return target[key]?.(this)
        return value
      },
      set: (target, key, value) => {
        if (isPrivateKey(key)) target[key] = value
        return true
      }
    })
  }

  /**
   * 子孙作用域集合
   */
  @reactive('computed')
  get descendants(): ViewScope[] {
    return this.children.flatMap((node) => [node, ...node.descendants])
  }

  /**
   * 实体父亲，一定有 ViewModel
   */
  get entity(): undefined | ViewScope {
    return this.element.entity?.scope
  }

  /**
   * 全局作用域(与 root 相等)
   */
  get global(): ViewScope {
    return this.root
  }

  /**
   * 当前索引
   */
  get index() {
    return this.element.index
  }

  /**
   * 多维索引
   */
  get indexes() {
    return this.element.indexes
  }

  /**
   * 视图模型
   */
  get model(): undefined | ViewModel {
    return this.element.model
  }

  /**
   * TypedNode
   */
  get node(): TypedNode {
    return this.element.node
  }

  /**
   * 父作用域节点
   */
  get parent(): undefined | ViewScope {
    return this.element.parent?.scope
  }

  /**
   * 父作用域集合(从自身父节点开始)
   */
  get parents(): ViewScope[] {
    if (this.parent) {
      return [this.parent, ...this.parent.parents]
    }
    return []
  }

  /**
   * 根作用域
   */
  get root(): ViewScope {
    return this.element.root.scope
  }

  /**
   * 状态中心
   */
  get store(): Store {
    return this.element.store
  }

  /**
   * 作用域类型
   */
  get type(): ScopeTypes {
    return this.typings.type
  }

  get typings() {
    return this.node.__r_typed_scope
  }

  @reactive('computed')
  get variables() {
    const accessors = this.typings.__r_accessors
    return new Proxy(accessors, {
      get: (target, key) => {
        if (isProxyRawKey(key)) return getProxyRaw(target)
        const value = target[key]
        if (typeof value === 'function') return target[key]?.(this)
        return value
      },
      set: (target, key, value) => {
        if (isPrivateKey(key)) target[key] = value
        return true
      }
    })
  }

  constructor(element: ViewElement) {
    super()
    this.element = element
    this.__r_initialized = true
  }

  override __r_getEventParent() {
    return this.getParent() ?? this.node.__r_store
  }

  override __r_getEventTarget() {
    return this.element
  }

  /**
   * 按照指定路径查找视图作用域
   * @param address
   */
  find(address?: string): undefined | ViewScope
  /**
   * 基于过滤器查找视图作用域
   * @param filter
   */
  find<T extends ViewScope = ViewScope>(filter?: Filter<T>): T | undefined
  find(arg?: any): undefined | ViewScope {
    if (typeof arg === 'string' || arg === undefined)
      return this.element.find(arg)?.scope
    if (typeof arg === 'function') {
      return this.element.find((element) => {
        return element.scope !== undefined && arg(element.scope)
      })?.scope
    }
  }

  /**
   * 基于过滤器查找视图作用域集合
   * @param filter
   */
  findAll<T extends ViewScope = ViewScope>(filter?: Filter<ViewScope>): T[] {
    const result: ViewScope[] = []
    for (const element of this.element.findAll()) {
      const scope = element.scope
      if (scope === undefined) continue
      if (filter === undefined) result.push(scope)
      else if (filter(scope)) result.push(scope)
    }
    return result as T[]
  }

  /**
   * 获取子节点集合
   */
  getChildren(): ViewScope[] {
    return this.children
  }

  /**
   * 获取子孙集合
   */
  getDescendants(): ViewScope[] {
    return this.descendants
  }

  /**
   * 获取实体父亲作用域模型
   * @returns
   */
  getEntity() {
    return this.entity
  }

  /**
   * 通过路径获取变量
   * @param path
   * @returns
   */
  getIn(path: PropertyKey[]) {
    return getIn(this.variables, path)
  }

  /**
   * 获取局部变量
   * @param name
   * @param recursive 是否递归往上查找
   */
  getLocalVariable(name: string, recursive = false): any {
    const variables = this.__r_local_variables
    if (recursive) {
      if (variables && name in variables) {
        return variables[name]
      }
      return this.parent?.getLocalVariable(name, recursive)
    }
    return variables?.[name]
  }

  /**
   * 获取所有局部变量
   */
  getLocalVariables() {
    return this.__r_local_variables
  }

  /**
   * 获取父作用域节点
   */
  getParent(): undefined | ViewScope {
    return this.parent
  }

  /**
   * 获取父作用域集合(从自身父节点开始)
   */
  getParents(): ViewScope[] {
    return this.parents
  }

  /**
   * 获取根节点
   */
  getRoot(): ViewScope {
    return this.root
  }

  /**
   * 获取状态中心
   */
  getStore(): Store {
    return this.store
  }

  /**
   * 是否是根节点
   */
  isRoot() {
    return this.parent === undefined
  }

  /**
   * 地址匹配器(支持 glob 语法)
   * @param pattern
   * @returns
   */
  match(pattern: PathLikes): boolean {
    return this.element.match(pattern)
  }

  override raise(error: Error): void {
    this.node.raise(error)
  }

  /**
   * 设置变量
   * @param variables
   */
  setLocalVariables(variables?: TRecord) {
    if (!variables) return
    this.__r_local_variables = variables
  }

  /**
   * 序列化视图作用域
   */
  toJS(): TRecord {
    return {
      type: this.type,
      variables: toJS(this.variables)
    }
  }

  /**
   * 序列化视图作用域
   */
  toJSON(): TRecord {
    return {
      type: this.type,
      variables: toJSON(this.variables)
    }
  }
}
